package com.dotdrives.web.pages.updates

import com.dotdrives.design.DotTone
import com.dotdrives.model.MonthlyGivingSummary
import com.dotdrives.model.ReportStatus
import kotlin.math.round
import kotlin.time.Instant

fun updateInitials(name: String?): String =
    (name ?: "").split(' ', '.')
        .filter(String::isNotBlank)
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "?" }

/**
 * Matches the design's compact age chips ("2H", "1D", "3W", "1M" -- `design/portal-updates.dc.html`
 * `u.when`/`t.when`). Never negative: a clock-skewed `occurredAt` in the future clamps to "NOW"
 * rather than printing a negative unit.
 */
fun relativeTimeLabel(occurredAt: Instant, now: Instant): String {
    val minutes = (now - occurredAt).inWholeMinutes.coerceAtLeast(0)
    val hours = minutes / 60
    val days = hours / 24
    val weeks = days / 7
    val months = days / 30
    return when {
        hours < 1 -> "NOW"
        hours < 24 -> "${hours}H"
        days < 7 -> "${days}D"
        days < 30 -> "${weeks.coerceAtLeast(1)}W"
        else -> "${months.coerceAtLeast(1)}M"
    }
}

data class StatusStyle(val label: String, val tone: DotTone)

fun reportStatusStyle(status: String): StatusStyle = when (status) {
    ReportStatus.SUBMITTED -> StatusStyle("Submitted", DotTone.Success)
    ReportStatus.SHARED -> StatusStyle("Shared", DotTone.Info)
    else -> StatusStyle("Draft", DotTone.Warning)
}

/** Abbreviated money for the summaries dashboard -- "$8K", "$1.2M". */
fun formatMoneyCompact(amount: Double): String {
    val abs = kotlin.math.abs(amount)
    val sign = if (amount < 0) "-" else ""
    return when {
        abs >= 1_000_000 -> "$sign$${trimDecimal(abs / 1_000_000)}M"
        abs >= 1_000 -> "$sign$${trimDecimal(abs / 1_000)}K"
        else -> "$sign$${abs.toLong()}"
    }
}

private fun trimDecimal(value: Double): String {
    val rounded = round(value * 10) / 10
    val asLong = rounded.toLong()
    return if (rounded == asLong.toDouble()) asLong.toString() else rounded.toString()
}

/** 0-100, clamped. A null or zero goal renders 0 rather than dividing by zero. */
fun targetProgressPercent(raised: Double, goal: Double?): Int {
    if (goal == null || goal <= 0.0) return 0
    return ((raised / goal) * 100).toInt().coerceIn(0, 100)
}

fun buildMonthlySummaryCsv(months: List<MonthlyGivingSummary>): String {
    val header = "Month,Gifts,Online,Manual,Recurring,Total"
    val rows = months.joinToString("\r\n") { month ->
        listOf(
            month.label,
            month.giftCount.toString(),
            money2(month.onlineAmount),
            money2(month.manualAmount),
            money2(month.recurringAmount),
            money2(month.totalAmount),
        ).joinToString(",")
    }
    return "$header\r\n$rows"
}

private fun money2(amount: Double): String {
    val cents = round(amount * 100).toLong()
    val wholePart = cents / 100
    val fractionPart = (cents % 100).let { if (it < 0) -it else it }
    return "$wholePart.${fractionPart.toString().padStart(2, '0')}"
}

/** Calendar choices stay anchored while navigating history. */
fun reportYearOptions(currentYear: Int, selectedYear: Int): List<Int> =
    ((currentYear downTo currentYear - 4).toList() + selectedYear).distinct().sortedDescending()
